import { useEffect, useMemo, useState } from 'react'
import {Eye, EyeOff, Filter, Plus, Search, ShieldCheck, Trash2, X} from 'lucide-react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import { useAppAuth } from '@/contexts/AuthContext'
import { formatDateTimeBR, maskCPF } from '@/utils/masks'
import { buildEscopoFilter, escopoLabel, filtrarPorEscopo } from '@/utils/escopo'
import StatusBadge, { statusBadge } from '@/components/protocolos/StatusBadge'
import NovoProtocoloModal from '@/components/protocolos/NovoProtocoloModal'
import AnaliseProtocoloModal from '@/components/protocolos/AnaliseProtocoloModal'

interface Protocolo {
  _id: string
  numeroProtocolo: string
  nomeCompleto: string
  cpf: string
  funcaoExercida: string
  localTrabalho: string
  dataAbonada: string
  datasAbonadas?: string[]
  coletivo?: boolean
  motivo: string
  descricaoProblema?: string
  descricaoOutro?: string
  status: string
  parecer?: string
  cidadeId?: string
  secretariaId?: string
  subLocalId?: string
  analisadoPorUserId?: string
  analisadoPorNome?: string
  analisadoEm?: string
  excluido?: boolean
  createdAt: string
}

const PAGE_SIZE = 10

function Protocolos() {
  const { user } = useAppAuth()
  const [protocolos, setProtocolos] = useState<Protocolo[]>([])
  const [loading, setLoading] = useState(true)
  const [showNovo, setShowNovo] = useState(false)
  const [selecionado, setSelecionado] = useState<Protocolo | null>(null)

  const [busca, setBusca] = useState('')
  const [statusFiltros, setStatusFiltros] = useState<string[]>([])
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [mostrarFiltros, setMostrarFiltros] = useState(false)
  const [page, setPage] = useState(1)
  const [cpfsVisiveis, setCpfsVisiveis] = useState<string[]>([])

  const alternarCpf = (id: string) => {
    setCpfsVisiveis(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const alternarStatusFiltro = (status: string) => {
    setStatusFiltros(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status])
    setPage(1)
  }

  const podeAnalisar = user?.role === 'ADMIN' || user?.role === 'MODERADOR'
  const podeExcluir = user?.role === 'ADMIN' || user?.role === 'MODERADOR'
  const podeVerExcluidos = user?.role === 'ADMIN' || user?.role === 'MODERADOR'
  const podeVerCpf = user?.role === 'ADMIN'

  const fetchData = async () => {
    setLoading(true)
    try {
      const filter: Record<string, any> = {
        excluido: { $ne: true },
        ...buildEscopoFilter(user),
      }

      const r = await (lumi.entities as any).protocolos.list({
        filter,
        sort: { createdAt: -1 },
        limit: 200,
      })
      setProtocolos(filtrarPorEscopo(r?.list || [], user))
    }
    catch {
      setProtocolos([])
    }
    finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [user?._id, user?.role, user?.cidadeId, user?.secretariaId])

  const [cidadeNome, setCidadeNome] = useState('')
  const [secretariaNome, setSecretariaNome] = useState('')

  useEffect(() => {
    const carregarNomes = async () => {
      try {
        if (user?.cidadeId) {
          const r = await (lumi.entities as any).cidades.list({ filter: { _id: user.cidadeId }, limit: 1 })
          setCidadeNome(r?.list?.[0]?.cidadeNome || r?.list?.[0]?.nome || '')
        }
        if (user?.secretariaId) {
          const r = await (lumi.entities as any).secretarias.list({ filter: { _id: user.secretariaId }, limit: 1 })
          setSecretariaNome(r?.list?.[0]?.nome || '')
        }
      }
      catch {
        /* silencioso */
      }
    }
    carregarNomes()
  }, [user?.cidadeId, user?.secretariaId])

  const filtrados = useMemo(() => {
    return protocolos.filter((p) => {
      if (statusFiltros.length && !statusFiltros.includes(p.status))
        return false
      if (dataInicio) {
        const dataProtocolo = p.createdAt ? p.createdAt.slice(0, 10) : ''
        if (!dataProtocolo || dataProtocolo < dataInicio)
          return false
      }
      if (dataFim) {
        const dataProtocolo = p.createdAt ? p.createdAt.slice(0, 10) : ''
        if (!dataProtocolo || dataProtocolo > dataFim)
          return false
      }
      if (busca) {
        const termo = busca.toLowerCase()
        const alvo = `${p.numeroProtocolo} ${p.nomeCompleto} ${p.cpf}`.toLowerCase()
        if (!alvo.includes(termo))
          return false
      }
      return true
    })
  }, [protocolos, busca, statusFiltros, dataInicio, dataFim])

  const filtrosAtivos = Boolean(statusFiltros.length || dataInicio || dataFim)

  const limparFiltros = () => {
    setStatusFiltros([])
    setDataInicio('')
    setDataFim('')
    setPage(1)
  }

  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / PAGE_SIZE))
  const paginaAtual = Math.min(page, totalPaginas)
  const paginaDados = filtrados.slice((paginaAtual - 1) * PAGE_SIZE, paginaAtual * PAGE_SIZE)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative w-full sm:w-[320px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" strokeWidth={1.75} />
          <input
            value={busca}
            onChange={(e) => { setBusca(e.target.value); setPage(1) }}
            placeholder="Buscar requerimento por número, nome ou CPF..."
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF] bg-white"
          />
        </div>

        <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 sm:gap-3">
          <button
            onClick={() => setMostrarFiltros(v => !v)}
            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-[13px] sm:text-[13.5px] font-medium border whitespace-nowrap ${mostrarFiltros || filtrosAtivos ? 'border-[#1E4FBF] text-[#1E4FBF] bg-[#EEF3FE]' : 'border-[#D1D5DB] text-[#374151] bg-white hover:bg-[#F5F6F8]'}`}
          >
            <Filter className="w-4 h-4" strokeWidth={1.75} />
            Filtros
            {filtrosAtivos && (
              <span className="w-1.5 h-1.5 rounded-full bg-[#1E4FBF]" />
            )}
          </button>

          {podeVerExcluidos && (
            <Link
              to="/protocolos/excluidos"
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-[13px] sm:text-[13.5px] font-medium border border-[#D1D5DB] text-[#374151] bg-white hover:bg-[#F5F6F8] whitespace-nowrap"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.75} />
              Excluídos
            </Link>
          )}

          <button
            onClick={() => setShowNovo(true)}
            className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 rounded-lg text-[13px] sm:text-[13.5px] font-medium bg-[#1E4FBF] hover:bg-[#1A44A6] text-white whitespace-nowrap ${podeVerExcluidos ? 'col-span-2 sm:col-span-1' : 'col-span-2 sm:col-span-1'}`}
          >
            <Plus className="w-4 h-4" strokeWidth={2} />
            Novo Requerimento
          </button>
        </div>
      </div>

      <div className="flex items-start gap-2.5 rounded-xl border border-[#E7E9EC] bg-[#FAFBFC] px-4 py-2.5">
        <ShieldCheck className="w-4 h-4 text-[#1E4FBF] mt-0.5 shrink-0" strokeWidth={1.9} />
        <p className="text-[12.5px] text-[#4B5563] leading-snug">
          {escopoLabel(user, cidadeNome, secretariaNome)}
        </p>
      </div>

      {mostrarFiltros && (
        <div className="bg-white rounded-2xl border border-[#E7E9EC] p-4 flex flex-col sm:flex-row sm:items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">Estado (selecione um ou mais)</label>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(statusBadge).map(([key, v]) => {
                const ativo = statusFiltros.includes(key)
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => alternarStatusFiltro(key)}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-medium border whitespace-nowrap transition-colors ${ativo ? 'border-[#1E4FBF] bg-[#1E4FBF] text-white' : 'border-[#D1D5DB] text-[#4B5563] bg-white hover:bg-[#F5F6F8]'}`}
                  >
                    {v.label}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex-1 min-w-[160px]">
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">Data inicial</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => { setDataInicio(e.target.value); setPage(1) }}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
          </div>

          <div className="flex-1 min-w-[160px]">
            <label className="block text-[12px] font-medium text-[#6B7280] mb-1.5">Data final</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => { setDataFim(e.target.value); setPage(1) }}
              className="w-full px-3 py-2.5 rounded-lg border border-[#D1D5DB] text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1E4FBF]/20 focus:border-[#1E4FBF]"
            />
          </div>

          {filtrosAtivos && (
            <button
              onClick={limparFiltros}
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-[13px] font-medium text-[#6B7280] hover:bg-[#F5F6F8] whitespace-nowrap"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
              Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Telemóvel: cartões */}
      <div className="md:hidden flex flex-col gap-2.5">
        {loading && (
          <div className="flex flex-col gap-2.5">
            {[0, 1, 2].map(i => <div key={i} className="h-[92px] rounded-xl bg-white border border-[#E7E9EC] animate-pulse" />)}
          </div>
        )}
        {!loading && paginaDados.length === 0 && (
          <div className="bg-white rounded-xl border border-[#E7E9EC] px-4 py-10 text-center text-[13px] text-[#9CA3AF]">
            Nenhum requerimento encontrado.
          </div>
        )}
        {!loading && paginaDados.map(p => (
          <button
            key={p._id}
            onClick={() => setSelecionado(p)}
            className="w-full text-left bg-white rounded-xl border border-[#E7E9EC] px-4 py-3 active:bg-[#F7F8FA] transition-colors"
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="text-[12px] font-semibold text-[#1E4FBF] tabular-nums">Nº {p.numeroProtocolo}</span>
              <StatusBadge status={p.status} comSelo />
            </div>
            <p className="text-[13.5px] font-medium text-[#12141A] truncate">
              {p.coletivo ? `Coletivo • ${p.localTrabalho}` : p.nomeCompleto}
            </p>
            <p className="text-[12px] text-[#6B7280] truncate mt-0.5">{p.motivo}</p>
            <p className="text-[11px] text-[#9CA3AF] mt-1.5 flex items-center gap-1.5">
              {!p.coletivo && (
                <>
                  <span className="tabular-nums">{cpfsVisiveis.includes(p._id) ? maskCPF(p.cpf) : '•••••'}</span>
                  {podeVerCpf && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); alternarCpf(p._id) }}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); alternarCpf(p._id) } }}
                      className="text-[#9CA3AF] hover:text-[#1E4FBF] p-0.5"
                      aria-label={cpfsVisiveis.includes(p._id) ? 'Ocultar CPF' : 'Mostrar CPF'}
                    >
                      {cpfsVisiveis.includes(p._id)
                        ? <EyeOff className="w-3.5 h-3.5" strokeWidth={1.9} />
                        : <Eye className="w-3.5 h-3.5" strokeWidth={1.9} />}
                    </span>
                  )}
                  <span>•</span>
                </>
              )}
              {formatDateTimeBR(p.createdAt)}
            </p>
          </button>
        ))}

        {!loading && filtrados.length > PAGE_SIZE && (
          <div className="flex items-center justify-between bg-white rounded-xl border border-[#E7E9EC] px-3 py-2.5">
            <span className="text-[11.5px] text-[#9CA3AF]">
              Página
              {' '}
              {paginaAtual}
              /
              {totalPaginas}
            </span>
            <div className="flex gap-2">
              <button
                disabled={paginaAtual <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium border border-[#D1D5DB] disabled:opacity-40 active:bg-[#F5F6F8]"
              >
                Anterior
              </button>
              <button
                disabled={paginaAtual >= totalPaginas}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium border border-[#D1D5DB] disabled:opacity-40 active:bg-[#F5F6F8]"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="hidden md:block bg-white rounded-2xl border border-[#E7E9EC] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#E7E9EC] bg-[#FAFBFC]">
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Nº Protocolo</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Requerente</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">CPF</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Motivo</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Data</th>
                <th className="px-5 py-3 text-[12px] font-semibold text-[#6B7280]">Estado</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F2F4]">
              {loading
                ? (
                    <tr>
                      <td colSpan={6} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">A carregar...</td>
                    </tr>
                  )
                : paginaDados.length === 0
                  ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-8 text-center text-[13px] text-[#9CA3AF]">Nenhum protocolo encontrado.</td>
                      </tr>
                    )
                  : paginaDados.map(p => (
                      <tr
                        key={p._id}
                        onClick={() => setSelecionado(p)}
                        className="hover:bg-[#F7F8FA] cursor-pointer transition-colors"
                      >
                        <td className="px-5 py-3.5 text-[13.5px] font-medium text-[#12141A]">{p.numeroProtocolo}</td>
                        <td className="px-5 py-3.5 text-[13.5px] text-[#12141A] max-w-[220px] truncate">
                          <span className="inline-flex items-center gap-1.5">
                            {p.coletivo && (
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#92400E] bg-[#FEF3C7] border border-[#FCD34D] rounded px-1.5 py-0.5 shrink-0">
                                Coletivo
                              </span>
                            )}
                            <span className="truncate">{p.coletivo ? p.localTrabalho : p.nomeCompleto}</span>
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-[13.5px] text-[#4B5563]">
                          {p.coletivo
                            ? '—'
                            : (
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="tabular-nums">{cpfsVisiveis.includes(p._id) ? maskCPF(p.cpf) : '•••••'}</span>
                                  {podeVerCpf && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); alternarCpf(p._id) }}
                                      className="text-[#9CA3AF] hover:text-[#1E4FBF] transition-colors"
                                      title={cpfsVisiveis.includes(p._id) ? 'Ocultar CPF' : 'Mostrar CPF'}
                                      aria-label={cpfsVisiveis.includes(p._id) ? 'Ocultar CPF' : 'Mostrar CPF'}
                                    >
                                      {cpfsVisiveis.includes(p._id)
                                        ? <EyeOff className="w-4 h-4" strokeWidth={1.9} />
                                        : <Eye className="w-4 h-4" strokeWidth={1.9} />}
                                    </button>
                                  )}
                                </span>
                              )}
                        </td>
                        <td className="px-5 py-3.5 text-[13px] text-[#4B5563] max-w-[220px] truncate">{p.motivo}</td>
                        <td className="px-5 py-3.5 text-[13px] text-[#4B5563] whitespace-nowrap">{formatDateTimeBR(p.createdAt)}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={p.status} comSelo /></td>
                      </tr>
                    ))}
            </tbody>
          </table>
        </div>

        {!loading && filtrados.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-[#E7E9EC]">
            <p className="text-[12.5px] text-[#9CA3AF]">
              Página
              {' '}
              {paginaAtual}
              {' '}
              de
              {' '}
              {totalPaginas}
              {' '}
              •
              {' '}
              {filtrados.length}
              {' '}
              resultados
            </p>
            <div className="flex gap-2">
              <button
                disabled={paginaAtual <= 1}
                onClick={() => setPage(p => p - 1)}
                className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-[#D1D5DB] disabled:opacity-40 hover:bg-[#F5F6F8]"
              >
                Anterior
              </button>
              <button
                disabled={paginaAtual >= totalPaginas}
                onClick={() => setPage(p => p + 1)}
                className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-[#D1D5DB] disabled:opacity-40 hover:bg-[#F5F6F8]"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {showNovo && (
        <NovoProtocoloModal onClose={() => setShowNovo(false)} onCreated={fetchData} />
      )}

      {selecionado && (
        <AnaliseProtocoloModal
          protocolo={selecionado}
          podeAnalisar={podeAnalisar}
          podeExcluir={podeExcluir}
          onClose={() => setSelecionado(null)}
          onUpdated={fetchData}
        />
      )}
    </div>
  )
}

export default Protocolos
